import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { CategoryLevelStyles, FeestageLevelStyles, HeaderStyles, NoDataMatchStyles, SubCategoryLevelStyles, TotalCostStyles } from "../../assets/styles/CommonStyles";
import { InvoiceTrackerFilters, ProposalInvoiceTracker } from "../../interfaces/InvoiceTracker.interface";
import { AddCircleIcon, CustomBackdrop, CustomButton, formatCurrency, CustomDatePicker, CustomTextField, RemoveCircleIcon, SearchBar, Toaster, useApi, useParams } from "../../utils/globalImports";
import { Delete, Filter1Outlined, FilterListOffOutlined, FilterListOutlined, FilterTiltShiftOutlined, SearchOutlined } from "@mui/icons-material";

const FixedColumns = [
  "Code",
  "Description",
  "Cost",
  "Qty",
  "Total Cost",
];

const CustomWidth = [180, 300, 120, 100, 150];

const InvoiceTracker: React.FC = () => {
  const [openProposal, setOpenProposal] = useState<Record<number, boolean>>({});
  const [openCategory, setOpenCategory] = useState<Record<number, boolean>>({});
  const [openSubCategory, setOpenSubCategory] = useState<Record<number, boolean>>({});
  const [data, setData] = useState<ProposalInvoiceTracker[]>([])
  const [filters, setFilters] = useState<InvoiceTrackerFilters>({
    startDate: null,
    endDate: null,
    searchText: ""
  })
  useEffect(() => {
    fetchInvoiceTracker(filters);

  }, [])
  const [TriggerClear, setTriggerClear] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });
  const { projectId } = useParams<{ projectId?: string }>();
  const fetchInvoiceTracker = async (
    currentFilters = filters
  ) => {
    try {

      const response =
        await execute<any>(
          "post",
          "/Project/InvoiceTracker",
          {
            projectId: Number(projectId),
            filters: currentFilters
          }
        );

      setData(
        Array.isArray(response.data?.data)
          ? response.data.data
          : []
      );
      // setTriggerClear(false);
      // handleClearFilters();
    } catch (ex) {
      const error = ex as any;
      setData([]);

      setToast({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to Load Data",
        type: "error",
      });
    }
  };
  const { execute, loading } = useApi();
  const handleClearFilters = () => {
    const newFilters={
      startDate: null,
      endDate: null,
      searchText: "",
    }
    setFilters(newFilters);
    setTriggerClear(true);

    fetchInvoiceTracker(newFilters);
  }
  const handleSearch = () => {
    fetchInvoiceTracker(filters);
  }
  const monthYearColumns = useMemo(() => {
    const map = new Map();

    data.forEach((proposal) =>
      proposal.categories?.forEach((category) =>
        category.invoiceSubCategory?.forEach((sub) =>
          sub.feeStages?.forEach((stage) =>
            stage.invoices?.forEach((inv) => {
              const d = new Date(inv.monthDate);
              const key = `${d.getFullYear()}-${d.getMonth()}`;

              if (!map.has(key)) {
                map.set(key, {
                  key,
                  label: d.toLocaleString("default", {
                    month: "short",
                    year: "numeric",
                  }),
                  date: d,
                });
              }
            })
          )
        )
      )
    );

    return Array.from(map.values()).sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }, [data]);
  const [showHiddenMonths, setShowHiddenMonths] =useState(false);
  const visibleMonthColumns = useMemo(() => {
  if (showHiddenMonths) {
    return monthYearColumns;
  }

  const today = new Date();

  const last5Months = monthYearColumns.filter((m) => {
    const diffMonths =
      (today.getFullYear() - m.date.getFullYear()) * 12 +
      (today.getMonth() - m.date.getMonth());

    return diffMonths >= 0 && diffMonths <= 5;
  });

  return last5Months;
}, [monthYearColumns, showHiddenMonths]);
  const getInvoiceValue = (invoices: any[], key: string) => {
    return (
      invoices.find((inv) => {
        const d = new Date(inv.monthDate);
        return `${d.getFullYear()}-${d.getMonth()}` === key;
      })?.invoicedValue ?? 0
    );
  };
  const getFeeStageMonthTotal = (feestages: any[], monthKey: string) => {
    return feestages.reduce((sum, stage) => {
      return (
        sum +
        (stage.invoices?.find((inv: any) => {
          const d = new Date(inv.monthDate);
          return `${d.getFullYear()}-${d.getMonth()}` === monthKey;
        })?.invoicedValue ?? 0)
      );
    }, 0);
  };

  const getSubCategoryTotals = (sub: any) => {
  const cost = sub.feeStages.reduce(
    (sum: number, stage: any) => sum + Number(stage.cost || 0),
    0
  );

  const totalCost = sub.feeStages.reduce(
    (sum: number, stage: any) => sum + Number(stage.totalCost || 0),
    0
  );

  const months: Record<string, number> = {};

  monthYearColumns.forEach((m) => {
    months[m.key] = getFeeStageMonthTotal(sub.feeStages, m.key);
  });

  return { cost, totalCost, months };
};
  const getCategoryTotals = (category: any) => {
  const allStages =
    category.invoiceSubCategory.flatMap(
      (s: any) => s.feeStages || []
    );

  const cost = allStages.reduce(
    (sum: number, stage: any) =>
      sum + Number(stage.cost || 0),
    0
  );

  const totalCost = allStages.reduce(
    (sum: number, stage: any) =>
      sum + Number(stage.totalCost || 0),
    0
  );

  const months: Record<string, number> = {};

  monthYearColumns.forEach((m) => {
    months[m.key] = getFeeStageMonthTotal(allStages, m.key);
  });

  return { cost, totalCost, months };
};

 const getProposalTotals = (proposal: any) => {
  const allStages =
    proposal.categories.flatMap((c: any) =>
      c.invoiceSubCategory.flatMap(
        (s: any) => s.feeStages || []
      )
    );

  const cost = allStages.reduce(
    (sum: number, stage: any) =>
      sum + Number(stage.cost || 0),
    0
  );

  const totalCost = allStages.reduce(
    (sum: number, stage: any) =>
      sum + Number(stage.totalCost || 0),
    0
  );

  const months: Record<string, number> = {};

  monthYearColumns.forEach((m) => {
    months[m.key] = getFeeStageMonthTotal(allStages, m.key);
  });

  return { cost, totalCost, months };
};
const grandTotals = useMemo(() => {
  const allStages = data.reduce((arr, proposal) => {
    proposal.categories.forEach((cat) => {
      cat.invoiceSubCategory.forEach((sub) => {
        arr.push(...(sub.feeStages || []));
      });
    });
    return arr;
  }, [] as any[]);

  const cost = allStages.reduce(
    (sum, stage) =>
      sum + Number(stage.cost || 0),
    0
  );

  const totalCost = allStages.reduce(
    (sum, stage) =>
      sum + Number(stage.totalCost || 0),
    0
  );

  const months: Record<string, number> = {};

  monthYearColumns.forEach((m) => {
    months[m.key] = getFeeStageMonthTotal(
      allStages,
      m.key
    );
  });

  return {
    cost,
    totalCost,
    months,
  };
}, [data, monthYearColumns]);

  const getStickyStyle = (index: number, bgColor: string = "#f3f3f3") => ({
    position: "sticky",
    left: CustomWidth.slice(0, index).reduce((a, b) => a + b, 0),
    zIndex: 1,
    minWidth: CustomWidth[index],
    width: CustomWidth[index],
    backgroundColor: bgColor,
  });
  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0eaf6",
          borderRadius: "14px",
          flexShrink:0

        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f0f6ff",
            borderBottom: "1px solid #e0eaf6",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >

            <FilterListOutlined
              sx={{
                fontSize: 18,
                color: "#1e66ab",
              }}
            />

            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#1e66ab",
              }}
            >
              Filters
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            p: 3,
            backgroundColor: "#f7faff",
          }}
        >
          <Grid2
            container
            spacing={2}
            alignItems="flex-end"
          >
            <Box>
              {/* Heading */}
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#888",
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Search & Filters
              </Typography>


              {/* Filters Row */}
              <Grid2
                container
                spacing={2}
                alignItems="center"
                wrap="nowrap"
              >
                {/* Search */}
                <Grid2 size={{ xs: 12, md: 8 }}>
                  <SearchBar
                    placeholder="Search By Proposal Number and Proposal Name"
                    onQueryChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        searchText: value,
                      }))
                    }
                    isClear={TriggerClear}
                    setIsClear={setTriggerClear}
                    sx={{
                      fontSize: "14px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                </Grid2 >
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                  <CustomDatePicker
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        startDate: value,
                      }))
                    }
                    height={43}
                    sx={{
                      borderRadius: "50px",
                      backgroundColor: '#fff'
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
                  <CustomDatePicker
                    label="End Date"
                    value={filters.endDate}
                    onChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        endDate: value,
                      }))
                    }
                    height={43}
                    sx={{
                      borderRadius: "50px",
                      backgroundColor: '#fff'
                    }}
                  />
                </Grid2>
                <Grid2 sx={{ ml: "auto" }}>
                  <CustomButton
                    capsule
                    sx={{
                      textTransform: "none",
                      height: "45px",
                      px: 3,
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleSearch}
                    backgroundColor="#1e66ab"
                  >
                    <SearchOutlined
                      sx={{
                        fontSize: 18,
                        mr: 0.5,
                      }}
                    />
                    Search
                  </CustomButton>

                </Grid2>

                <Grid2 sx={{ ml: "auto" }}>
                  <CustomButton
                    capsule
                    sx={{
                      textTransform: "none",
                      height: "45px",
                       p:2,
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleClearFilters}
                    backgroundColor='#92d050'
                  >
                    Clear All
                  </CustomButton>

                </Grid2>
              </Grid2>
            </Box>

          </Grid2>
        </Box>
      </Paper>
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box display="flex" justifyContent="left">
                  <IconButton
                    color="primary"
                    aria-label="toggle months"
                    onClick={() => setShowHiddenMonths(prev => !prev)}
                  >
                    {showHiddenMonths ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <RemoveCircleIcon sx={{ color: '#a3c63d' }} />
                        <Typography variant="body2">Hide Invoiced Months</Typography>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        <AddCircleIcon sx={{ color: '#a3c63d' }} />
                        <Typography variant="body2">Show Invoiced Months</Typography>
                      </Box>
                    )}
                  </IconButton>
                </Box>
              </Box>
      <TableContainer sx={{ 
                       overflow: "auto", minHeight: 0 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {FixedColumns.map((col, i) => (
                <TableCell
                  key={col}
                  sx={{
                    ...getStickyStyle(i, "#92d050"),
                    fontsize: "16px",
                    fontWeight: "bold",
                    border: "1px solid rgba(0,0,0,0.04)",
                    zIndex: 10

                  }}
                >
                  {col}
                </TableCell>
              ))}

              {visibleMonthColumns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    ...HeaderStyles,
                    minWidth: 150,
                    textAlign: "left",
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={FixedColumns.length + visibleMonthColumns.length}
                  sx={{
                    color: '#1e66ab',
                    fontSize: '14px',
                    textAlign:"center"
               
                  }}
                >
                 
                No data matching the search criteria
    
                </TableCell>
              </TableRow>
            ) : (
              <>
                {data.map((proposal) => {
                  const proposalTotals =getProposalTotals(proposal);
                  return(
                  <React.Fragment key={proposal.proposalID}>
                    {/* Proposal */}
                    <TableRow>
                      <TableCell
                        sx={{
                          ...getStickyStyle(0)
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            setOpenProposal((prev) => ({
                              ...prev,
                              [proposal.proposalID]: !prev[proposal.proposalID],
                            }))
                          }
                        >
                          {openProposal[proposal.proposalID] ? (
                            <KeyboardArrowDownIcon />
                          ) : (
                            <KeyboardArrowRightIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          ...getStickyStyle(1),
                          fontWeight: 700,
                          color: "#1e66ab",
                          fontSize: 18,
                        }}
                      >
                        {proposal.proposalNumber}
                      </TableCell>

                      <TableCell sx={{ ...getStickyStyle(2),fontWeight: 700,
                          color: "#1e66ab",
                          fontSize: 18, }}> {formatCurrency(proposalTotals.cost)}</TableCell>
                      <TableCell sx={{ ...getStickyStyle(3) }} />
                     <TableCell sx={{...getStickyStyle(4),fontWeight: 700,
                          color: "#1e66ab",
                          fontSize: 18}}>{formatCurrency(proposalTotals.totalCost)}</TableCell>

                      {visibleMonthColumns.map((m) => (
                        <TableCell key={m.key} sx={{ backgroundColor: "#f3f3f3",fontWeight: 700,
                          color: "#1e66ab",
                          fontSize: 18 }} > 
            {formatCurrency(proposalTotals.months[m.key])}</TableCell>
                      ))}
                    </TableRow>
                    {openProposal[proposal.proposalID] &&
                      proposal.categories.map((category) => {
                        const categoryTotals =getCategoryTotals(category);
                        return(
                        <React.Fragment key={category.categoryID}>
                          {/* Category */}
                          <TableRow>
                            <TableCell sx={{ ...getStickyStyle(0) }} />

                            <TableCell
                              sx={{
                                ...CategoryLevelStyles,
                                ...getStickyStyle(1),
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setOpenCategory((prev) => ({
                                    ...prev,
                                    [category.categoryID]: !prev[category.categoryID],
                                  }))
                                }
                              >
                                {openCategory[category.categoryID] ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}
                              </IconButton>

                              {category.categoryName}
                            </TableCell>

                            <TableCell sx={{ ...getStickyStyle(2),...CategoryLevelStyles }} >{formatCurrency(categoryTotals.cost)}</TableCell>
                            <TableCell sx={{ ...getStickyStyle(3),...CategoryLevelStyles }} />
                            <TableCell sx={{ ...getStickyStyle(4),...CategoryLevelStyles }} >{formatCurrency(categoryTotals.totalCost)}</TableCell>

                            {visibleMonthColumns.map((m) => (
                              <TableCell key={m.key} sx={{ ...CategoryLevelStyles }}>{formatCurrency(categoryTotals.months[m.key])}</TableCell> 
                            ))}
                          </TableRow>

                          {/* FeeStages */}
                          {/* Category Children */}
                          {openCategory[category.categoryID] &&
                            category.invoiceSubCategory.map((sub) => {
                              const subTotals =getSubCategoryTotals(sub);
                              return(
                              <React.Fragment key={sub.subcategoryID}>
                                {/* SubCategory / SubStage */}
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      ...getStickyStyle(0),
                                    }}
                                  />

                                  <TableCell
                                    sx={{
                                      ...SubCategoryLevelStyles,
                                      ...getStickyStyle(1),
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setOpenSubCategory((prev) => ({
                                          ...prev,
                                          [sub.subcategoryID]:
                                            !prev[sub.subcategoryID],
                                        }))
                                      }
                                    >
                                      {openSubCategory[sub.subcategoryID] ? (
                                        <KeyboardArrowDownIcon />
                                      ) : (
                                        <KeyboardArrowRightIcon />
                                      )}
                                    </IconButton>

                                    {sub.subcategoryName}
                                  </TableCell>

                                  <TableCell sx={{ ...getStickyStyle(2),...SubCategoryLevelStyles} } >{formatCurrency(subTotals.cost)}</TableCell>
                                  <TableCell sx={{ ...getStickyStyle(3) }} ></TableCell>
                                  <TableCell sx={{ ...getStickyStyle(4),...SubCategoryLevelStyles}} >{formatCurrency(subTotals.totalCost)}</TableCell>

                                  {visibleMonthColumns.map((m) => (
                                    <TableCell
                                      key={m.key}
                                      sx={{ ...SubCategoryLevelStyles }}
                                    >{formatCurrency(subTotals.months[m.key])}</TableCell>
                                  ))}
                                </TableRow>

                                {/* FeeStages */}
                                {openSubCategory[sub.subcategoryID] &&
                                  sub.feeStages.map((stage) => (
                                    <TableRow key={stage.feeStageID}>
                                      <TableCell
                                        sx={{
                                          ...FeestageLevelStyles,
                                          ...getStickyStyle(0),
                                        }}
                                      >
                                        {stage.feeStageReferenceNumber}
                                      </TableCell>

                                      <TableCell
                                        sx={{
                                          ...FeestageLevelStyles,
                                          ...getStickyStyle(1),
                                        }}
                                      >
                                        {stage.description}
                                      </TableCell>

                                      <TableCell
                                        sx={{
                                          ...FeestageLevelStyles,
                                          ...getStickyStyle(2),

                                        }}
                                      >
                                        {formatCurrency(stage.cost)}
                                      </TableCell>

                                      <TableCell
                                        sx={{
                                          ...FeestageLevelStyles,
                                          ...getStickyStyle(3),

                                        }}
                                      >
                                        {stage.quantity}
                                      </TableCell>

                                      <TableCell
                                        sx={{
                                          ...FeestageLevelStyles,
                                          ...getStickyStyle(4),

                                        }}
                                      >
                                        {formatCurrency(stage.totalCost)}
                                      </TableCell>

                                      {visibleMonthColumns.map((month) => (
                                        <TableCell
                                          key={month.key}
                                          sx={FeestageLevelStyles}
                                        >
                                          {formatCurrency(getInvoiceValue(stage.invoices, month.key))}
                                          
                                        </TableCell>
                                      ))}
                                    </TableRow>

                                  ))}
                              </React.Fragment>)
                      })}
                        </React.Fragment>)
                })}
                  </React.Fragment>)
                })}
                      <TableRow
        sx={TotalCostStyles}
      >
        <TableCell
          sx={{
            ...getStickyStyle(0),
           ...TotalCostStyles, bottom: 0,
            zIndex: 101,
          }}
        />

        <TableCell
          sx={{
            ...getStickyStyle(1),
           ...TotalCostStyles,bottom: 0,
            zIndex: 101,
          }}
        >
          Total
        </TableCell>

        {/* Cost Total */}
        <TableCell
          sx={{
            ...getStickyStyle(2),
           ...TotalCostStyles,bottom: 0,
            zIndex: 101,
          }}
        >
          {formatCurrency(grandTotals.cost)}
        </TableCell>

        {/* Qty */}
        <TableCell
          sx={{
            ...getStickyStyle(3),bottom:0,zIndex: 101
          }}
        />

        {/* Total Cost */}
        <TableCell
          sx={{
            ...getStickyStyle(4),
           ...TotalCostStyles,bottom: 0,
            zIndex: 101,
          }}
        >
          {formatCurrency(grandTotals.totalCost)}
        </TableCell>

        {/* Month Totals */}
        {visibleMonthColumns.map((m) => (
          <TableCell
            key={m.key}
            sx={{...TotalCostStyles,position:"sticky",bottom: 0,
            zIndex: 10}}
          >
            
            {formatCurrency(grandTotals.months[
              m.key
            ])}
          </TableCell>
        ))}
      </TableRow></>)}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomBackdrop open={loading} />
      <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as "success" | "error"}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

export default InvoiceTracker;